import type { VoidFunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PublishButton from '../../shared/components/PublishButton';
import type { ProjectGroupContext, ReportCategory } from '../../shared/types';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption } from '../shared/types';
import { get } from '../../../../../redux/api';
import { getPublishURL, getSBOMPortalReportType } from '../releaseGroupLevel/utils';
import * as ReportGenerationStore from '../../../../../redux/reportGeneration';
import { useAddToast } from '../../../../../contexts/ToastContext';

type Props = {
  isDisabled?: boolean;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  authorName?: string;
  authorEmail?: string;
  supplier?: string;
};

/**
 * Opens a modal to confirm and publish the report.
 * The modal also provides a link to the SBOM portal and a link to the organization settings.
 */
const PublishReportButton: VoidFunctionComponent<Props> = ({
  isDisabled,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
  authorName,
  authorEmail,
  supplier,
}) => {
  const addToast = useAddToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const reportContext = useSelector(ReportGenerationStore.selectors.getReportContext) as ProjectGroupContext;
  useEffect(() => {
    setIsPublished(false);
  }, [format, reportType]);
  // Don't show the publish button if the report type is not supported
  // We currently only support SBOM reports and licensing plain text reports
  const sbomPortalReportType = getSBOMPortalReportType(reportType, format);
  if (!sbomPortalReportType) {
    return null;
  }

  const onPublishReport = async () => {
    setIsLoading(true);
    try {
      await get(
        getPublishURL({
          groupId: reportContext.groupId,
          releaseId: reportContext.releaseId,
          reportType,
          format,
          includeOptions,
          dependencyMetadataOptions,
          authorName,
          authorEmail,
          supplier,
        }) || '',
      );
    } catch (e) {
      addToast({ type: 'error', content: 'Failed to publish report' });
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
    setIsPublished(true);
  };

  return (
    <PublishButton
      isDisabled={isDisabled}
      onPublishReport={onPublishReport}
      reportType={sbomPortalReportType}
      isLoading={isLoading}
      isPublished={isPublished}
      replaceReportText="Republish"
      publishReportText="Publish"
      publishedReportText="Published"
    />
  );
};

export default PublishReportButton;
